import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, CalendarCheck2 } from "lucide-react";
import Logo from "@/components/Logo";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/for-schools", label: "For Schools" },
  { to: "/for-parents", label: "For Parents" },
  { to: "/dental-van", label: "Our Dental Van" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E5F0FA]"
      data-testid="site-header"
    >
      <div className="container-page flex items-center justify-between h-20">
        <Logo />

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-1"
          aria-label="Primary navigation"
          data-testid="desktop-nav"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-[#1677C8] bg-[#F4F9FF]"
                    : "text-[#14324A] hover:text-[#1677C8] hover:bg-[#F4F9FF]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/for-schools#enquiry"
            className="hidden md:inline-flex btn-accent text-sm md:text-base"
            data-testid="header-book-visit-btn"
          >
            <CalendarCheck2 size={18} strokeWidth={2.5} />
            Book a School Visit
          </Link>

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#F4F9FF] border border-[#E5F0FA] text-[#14324A]"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          id="mobile-menu"
          className="lg:hidden border-t border-[#E5F0FA] bg-white"
          data-testid="mobile-menu"
        >
          <nav className="container-page py-4 flex flex-col gap-1" aria-label="Mobile navigation">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl text-base font-medium transition-colors ${
                    isActive
                      ? "bg-[#F4F9FF] text-[#1677C8]"
                      : "text-[#14324A] hover:bg-[#F4F9FF]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/for-schools#enquiry"
              className="btn-accent mt-3"
              data-testid="mobile-book-visit-btn"
            >
              <CalendarCheck2 size={18} strokeWidth={2.5} />
              Book a School Visit
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
